/**
 * INTAKE FORM SCHEMA — the complete field inventory from the firm's paper
 * "New Client Intake Form", expressed declaratively.
 *
 * This file owns the form's STRUCTURE: which sections exist, which fields live in them,
 * and which fields are revealed by which answers (`visibleIf`).
 *
 * It deliberately does NOT own whether a field is required — that comes from
 * `settings/fieldRules` in Firestore so an admin can change it without a deploy.
 * See lib/fieldRules.js for how the two are merged at render time.
 *
 * Field shape:
 *   path       dotted path inside the matter document (also the fieldRules key)
 *   label      visible label
 *   type       text | textarea | number | money | percent | date | email | tel |
 *              yesno | radio | checkbox | multicheck | tags | repeatable |
 *              areaOfLawSelect | userSelect | peopleMultiSelect | optionsMultiSelect | practiceGroupSelect | originationSplit | attachments
 *   visibleIf  declarative condition evaluated against the whole matter (lib/conditions.js)
 *   options    for radio/multicheck/select
 *   fields     for repeatable rows
 *   maxLength / min / max / colSpan  presentation + validation hints
 *   fullWidth  admin-editable full-row counterpart to the legacy colSpan: 2
 *   newRow     begin this half-width field on a fresh desktop grid row
 */

export const CLIENT_SOURCE_OPTIONS = [
  { value: 'current_client', label: 'Current client' },
  { value: 'former_client', label: 'Former client' },
  { value: 'marketing_efforts', label: 'Marketing efforts' },
  { value: 'referred_by', label: 'Referred by' },
  {
    value: 'firm_employee_partner',
    label: 'Firm employee/partner *',
    note: 'Requires Managing Partner or Practice Group Leader approval',
  },
  {
    value: 'friend_of_firm_employee_partner',
    label: 'Friend of firm employee/partner *',
    note: 'Requires Managing Partner or Practice Group Leader approval',
  },
  {
    value: 'family_of_firm_employee_partner',
    label: 'Family member of firm employee/partner *',
    note: 'Requires Managing Partner or Practice Group Leader approval',
  },
];

const PARTY_ROLE_OPTIONS = [
  { value: 'officer', label: 'Officer' },
  { value: 'director', label: 'Director' },
  { value: 'member', label: 'Member' },
  { value: 'partner', label: 'Partner' },
  { value: 'shareholder', label: 'Shareholder' },
  { value: 'manager', label: 'Manager' },
  { value: 'employee_contact', label: 'Employee contact' },
  { value: 'other', label: 'Other' },
];

const isLitigation = { path: 'matterType.isLitigation', op: 'is_true' };
const isNonLitigation = { path: 'matterType.isNonLitigation', op: 'is_true' };

export const FORM_SECTIONS = [
  // ── 1 ───────────────────────────────────────────────────────────────────
  {
    id: 'parties',
    title: 'Parties',
    description:
      'List every name and entity to be sent to the Conflicts distribution group for review before this matter can proceed.',
    fields: [
      {
        path: 'parties.list',
        label: 'Names and entities for conflict review',
        type: 'repeatable',
        colSpan: 2,
        addLabel: 'Add name or entity',
        fields: [
          { path: 'name', label: 'Name', type: 'text' },
          {
            path: 'entityType',
            label: 'Type',
            type: 'radio',
            options: [
              { value: 'individual', label: 'Individual' },
              { value: 'entity', label: 'Entity' },
            ],
          },
          { path: 'role', label: 'Role (optional)', type: 'text' },
          { path: 'notes', label: 'Notes (optional)', type: 'textarea' },
        ],
      },
    ],
  },

  // ── 2 ───────────────────────────────────────────────────────────────────
  {
    id: 'header',
    title: 'Header & Billing',
    description: 'Billing set-up, responsible attorneys and the engagement letter.',
    fields: [
      { path: 'header.date', label: 'Date', type: 'date' },
      { path: 'header.practiceGroupId', label: 'Practice Group', type: 'practiceGroupSelect',
        help: 'Determines which Practice Group Leader approves this matter.' },
      { path: 'header.additionalPracticeGroupIds', label: 'Additional Practice Groups', type: 'practiceGroupMultiSelect',
        help: 'Select any additional practice groups that should be associated with this matter.' },
      { path: 'header.practiceGroupLeaderName', label: 'Practice Group Leader', type: 'text',
        help: 'Auto-filled from the selected practice group; override only if needed.' },
      { path: 'header.responsiblePartnerUid', label: 'Responsible Partner', type: 'userSelect' },
      { path: 'header.retainerAmount', label: 'Retainer Amount', type: 'money' },
      { path: 'header.adminFee', label: 'Admin Fee?', type: 'yesno' },
      { path: 'header.isNewClient', label: 'New client (needs a new client number)', type: 'checkbox' },
      { path: 'header.isNewMatter', label: 'New matter (needs a new matter number)', type: 'checkbox' },
      {
        path: 'notice.engagementLetterRequired',
        label: 'Engagement letter required notice',
        type: 'notice',
        variant: 'warning',
        text:
          'A SIGNED ENGAGEMENT LETTER IS REQUIRED FOR ALL CLIENTS AND MATTERS BEFORE A BILLING NUMBER IS ASSIGNED.',
      },
      { path: 'header.engagementLetter.signedOnFile', label: 'Signed engagement letter on file?', type: 'yesno' },
      {
        path: 'attachments.engagementLetterInline',
        label: 'Signed engagement letter',
        type: 'attachments',
        category: 'engagement_letter',
        engagementLetterInline: true,
        colSpan: 2,
        visibleIf: { path: 'header.engagementLetter.signedOnFile', op: 'is_true' },
        help: 'Upload the signed letter now, or use the Attachments section later. PDF, DOC, and DOCX files are accepted.',
      },
      {
        path: 'header.engagementLetter.waived',
        label: 'Is the engagement letter being waived?',
        type: 'yesno',
        visibleIf: { path: 'header.engagementLetter.signedOnFile', op: 'is_false' },
        help: 'This routes to the Managing Partner as a real approval step — no separate sign-off needed here.',
      },
      {
        path: 'header.engagementLetter.waiverReason',
        label: 'Reason for waiving',
        type: 'textarea',
        visibleIf: { path: 'header.engagementLetter.waived', op: 'is_true' },
      },
    ],
  },

  // ── 3 ───────────────────────────────────────────────────────────────────
  {
    id: 'client',
    title: 'Client Information',
    fields: [
      { path: 'client.name', label: 'Name of Client (business or individual)', type: 'text', colSpan: 2 },
      { path: 'client.address.street', label: 'Street Address', type: 'text', colSpan: 2 },
      { path: 'client.address.city', label: 'City', type: 'text', width: 'third' },
      { path: 'client.address.state', label: 'State', type: 'text', width: 'third' },
      { path: 'client.address.zip', label: 'ZIP', type: 'text', width: 'third' },
      { path: 'client.contactName', label: 'Client Contact Name', type: 'text' },
      { path: 'client.contactTitle', label: 'Contact Title', type: 'text' },
      { path: 'client.telephone', label: 'Telephone', type: 'tel' },
      { path: 'client.facsimile', label: 'Facsimile', type: 'tel' },
      { path: 'client.invoiceTo', label: 'Send Invoice To', type: 'text' },
      { path: 'client.invoiceEmail', label: 'Invoice Email Address', type: 'email' },

      { path: 'client.isFlatFee', label: 'Is this a Flat Fee?', type: 'yesno' },
      {
        path: 'client.flatFeeAmount',
        label: 'Flat Fee Amount',
        type: 'money',
        visibleIf: { path: 'client.isFlatFee', op: 'is_true' },
      },
      {
        path: 'client.flatFeeFor',
        label: 'For:',
        type: 'textarea',
        visibleIf: { path: 'client.isFlatFee', op: 'is_true' },
        help: 'What the flat fee covers.',
      },
      {
        path: 'client.hourlyRateQuoted',
        label: 'Actual Hourly Rate Quoted to Client',
        type: 'text',
        help:
          'Free text is fine — e.g. standard rates, a range, or partner/associate rates.',
        visibleIf: { path: 'client.isFlatFee', op: 'is_false' },
      },

      { path: 'client.isInsuranceMatter', label: 'Is this an Insurance Matter?', type: 'yesno' },
      { path: 'client.insurance.company', label: 'Insurance Company', type: 'text',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.type', label: 'Insurance Type', type: 'text',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.billingRates', label: 'Insurance Billing Rates', type: 'money',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.claimNo', label: 'Claim No.', type: 'text',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.carrierContact', label: 'Carrier Contact', type: 'text',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.carrierPhone', label: 'Carrier Phone No.', type: 'tel',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.carrierEmail', label: 'Carrier Email', type: 'email',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.dateOfTender', label: 'Date of Tender', type: 'date',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },
      { path: 'client.insurance.retentionAmount', label: 'Retention Amount', type: 'money',
        visibleIf: { path: 'client.isInsuranceMatter', op: 'is_true' } },

      { path: 'client.abaTaskCodes', label: 'ABA Task Codes?', type: 'yesno' },
      {
        path: 'client.abaTaskCodesList',
        label: 'ABA Task Codes',
        type: 'text',
        colSpan: 2,
        help: 'Enter the codes separated by commas, e.g. L110, L120, A103.',
        visibleIf: { path: 'client.abaTaskCodes', op: 'is_true' },
      },
    ],
  },

  // ── 4 ───────────────────────────────────────────────────────────────────
  {
    id: 'representation',
    title: 'Nature of Representation',
    fields: [
      {
        path: 'representation.description',
        label: 'Brief description of case or nature of work',
        type: 'textarea',
        rows: 5,
        colSpan: 2,
      },
    ],
  },

  // ── 5 ───────────────────────────────────────────────────────────────────
  {
    id: 'wallBuilder',
    title: 'Wall Builder Designation',
    description:
      'Firm policy requires identifying who may access this file. Check every practice group that should have access, and search for any individual people who need access outside of those groups.',
    fields: [
      {
        path: 'wallBuilder.practiceGroupIds',
        label: 'Practice groups permitted access',
        type: 'practiceGroupMultiSelect',
        colSpan: 2,
      },
      {
        path: 'wallBuilder.personUids',
        label: 'Individual people permitted access',
        type: 'personSearch',
        colSpan: 2,
      },
      { path: 'wallBuilder.notes', label: 'Notes', type: 'textarea', colSpan: 2 },
    ],
  },

  // ── 6 ───────────────────────────────────────────────────────────────────
  {
    id: 'clientSource',
    title: 'Client Source',
    fields: [
      {
        path: 'clientSource.source',
        label: 'Source of client',
        type: 'radio',
        options: CLIENT_SOURCE_OPTIONS,
        colSpan: 2,
      },
      {
        path: 'clientSource.referredBy',
        label: 'Referred by',
        type: 'text',
        visibleIf: { path: 'clientSource.source', op: 'eq', value: 'referred_by' },
      },
      {
        path: 'clientSource.formerClientNumber',
        label: 'If former client, what was the client number?',
        type: 'text',
        visibleIf: { path: 'clientSource.source', op: 'eq', value: 'former_client' },
      },
      {
        path: 'clientSource.paymentProblems',
        label: 'Have we had problems with this client paying within 30–60 days?',
        type: 'yesno',
        colSpan: 2,
        visibleIf: { path: 'header.isNewClient', op: 'is_false' },
      },
      {
        path: 'clientSource.hasReceivable',
        label: 'Do we have a receivable for this client?',
        type: 'yesno',
        newRow: true,
        visibleIf: { path: 'header.isNewClient', op: 'is_false' },
      },
      {
        path: 'clientSource.receivableAmount',
        label: 'Amount outstanding',
        type: 'money',
        // This is a dependent answer, not a peer question. Keep it off the
        // same grid row so the form reads as a clear vertical sequence.
        newRow: true,
        visibleIf: {
          all: [
            { path: 'header.isNewClient', op: 'is_false' },
            { path: 'clientSource.hasReceivable', op: 'is_true' },
          ],
        },
      },
    ],
  },

  // ── 7 ───────────────────────────────────────────────────────────────────
  {
    id: 'priorRepresentation',
    title: 'Prior Representation',
    fields: [
      {
        path: 'priorRepresentation.previouslyRepresented',
        label:
          'Has this potential client previously been represented by legal counsel in this matter?',
        type: 'yesno',
        colSpan: 2,
        help: 'If Yes, Managing Partner or Practice Group Leader approval is required before the file opens.',
      },
      {
        path: 'priorRepresentation.whyChangedCounsel',
        label: 'Why did the client change counsel?',
        type: 'textarea',
        colSpan: 2,
        visibleIf: { path: 'priorRepresentation.previouslyRepresented', op: 'is_true' },
      },
      {
        path: 'priorRepresentation.contactedFormerCounsel',
        label: "Have you contacted the client's former/current counsel or otherwise investigated?",
        type: 'yesno',
        visibleIf: { path: 'priorRepresentation.previouslyRepresented', op: 'is_true' },
      },
      {
        path: 'priorRepresentation.whatDisclosed',
        label: 'What did it disclose?',
        type: 'textarea',
        colSpan: 2,
        visibleIf: {
          all: [
            { path: 'priorRepresentation.previouslyRepresented', op: 'is_true' },
            { path: 'priorRepresentation.contactedFormerCounsel', op: 'is_true' },
          ],
        },
      },
      {
        path: 'priorRepresentation.litigationWithFormerAttorney',
        label:
          'Has this potential client ever been involved in litigation with a former attorney or law firm?',
        type: 'yesno',
        colSpan: 2,
        help: 'Informational — feeds the conflict check.',
      },
    ],
  },

  // ── 8 ───────────────────────────────────────────────────────────────────
  {
    id: 'conflictCheck',
    title: 'Conflict Check',
    description:
      'Captured as a structured party list so it can power a conflict-search feature later.',
    fields: [
      {
        path: 'conflictCheck.hitsFound',
        label: 'Were any conflict hits found?',
        type: 'yesno',
        help: 'Set by Intake after running the conflict report in Aderant.',
      },
      {
        path: 'notice.conflictHitsFound',
        label: 'Conflict hits found notice',
        type: 'notice',
        variant: 'warning',
        text:
          'HITS FOUND: Before proceeding you must alert the Requesting Attorney of the conflict search results.',
        visibleIf: { path: 'conflictCheck.hitsFound', op: 'is_true' },
      },
      {
        path: 'conflictCheck.hitsAcknowledgedByAttorney',
        label: 'I confirm the Requesting Attorney has been alerted to the conflict search results',
        type: 'checkbox',
        visibleIf: { path: 'conflictCheck.hitsFound', op: 'is_true' },
      },
      { path: 'conflictCheck.clientFullName', label: "Client's Full Name", type: 'text', colSpan: 2 },
      {
        path: 'conflictCheck.entityType',
        label: 'Client entity type',
        type: 'radio',
        options: [
          { value: 'individual', label: 'Individual' },
          { value: 'corporation_llc', label: 'Corporation / LLC' },
          { value: 'partnership', label: 'Partnership' },
          { value: 'public_entity', label: 'Public entity' },
          { value: 'other', label: 'Other' },
        ],
        colSpan: 2,
      },
      {
        path: 'conflictCheck.officersDirectorsMembers',
        label: 'Officers, directors and members',
        type: 'repeatable',
        colSpan: 2,
        visibleIf: { path: 'conflictCheck.entityType', op: 'in', value: ['corporation_llc', 'other', 'public_entity'] },
        addLabel: 'Add officer/director/member',
        fields: [
          { path: 'name', label: 'Name', type: 'text' },
          { path: 'role', label: 'Role', type: 'select', options: PARTY_ROLE_OPTIONS },
          { path: 'relatedBusinessNames', label: 'Related businesses', type: 'tags' },
        ],
      },
      {
        path: 'conflictCheck.primaryContact',
        label: 'Primary contact (if not an officer or member)',
        type: 'text',
        colSpan: 2,
      },
      {
        path: 'conflictCheck.partners',
        label: 'If a partnership: all partners',
        type: 'repeatable',
        colSpan: 2,
        visibleIf: { path: 'conflictCheck.entityType', op: 'eq', value: 'partnership' },
        addLabel: 'Add partner',
        help:
          'If a partner is itself an entity, also list its shareholders, officers, managers or members.',
        fields: [
          { path: 'name', label: 'Partner name', type: 'text' },
          { path: 'isEntity', label: 'Partner is an entity', type: 'checkbox' },
          { path: 'relatedBusinessNames', label: 'Its shareholders/officers/managers/members', type: 'tags' },
        ],
      },
      {
        path: 'conflictCheck.ownsOtherBusinesses',
        label: 'Does the client or any individual listed above own other businesses or subsidiaries?',
        type: 'yesno',
        colSpan: 2,
      },
      {
        path: 'conflictCheck.otherBusinesses',
        label: 'Names of those businesses',
        type: 'tags',
        colSpan: 2,
        visibleIf: { path: 'conflictCheck.ownsOtherBusinesses', op: 'is_true' },
      },
    ],
  },

  // ── 9 ───────────────────────────────────────────────────────────────────
  {
    id: 'matterType',
    title: 'Matter Type',
    description:
      'Choose litigation or non-litigation — a matter is one or the other, not both. This controls the next sections.',
    fields: [
      {
        path: 'matterType.selection',
        label: 'Matter Type',
        type: 'matterTypeSelect',
        colSpan: 2,
        options: [
          { value: 'non_litigation', label: 'Non-Litigation' },
          { value: 'litigation', label: 'Litigation' },
        ],
      },
    ],
  },

  // ── 9 ───────────────────────────────────────────────────────────────────
  {
    id: 'nonLitigation',
    title: 'Non-Litigation',
    visibleIf: isNonLitigation,
    fields: [
      {
        path: 'nonLitigation.adversarial',
        label:
          'Does this matter involve work adversarial or prejudicial to the interests of other individuals or entities?',
        type: 'yesno',
        colSpan: 2,
      },
      {
        path: 'nonLitigation.adverseParties',
        label: 'Names of those individuals/entities',
        type: 'repeatable',
        colSpan: 2,
        visibleIf: { path: 'nonLitigation.adversarial', op: 'is_true' },
        addLabel: 'Add individual/entity',
        fields: [
          { path: 'name', label: 'Name', type: 'text' },
          { path: 'isEntity', label: 'Is an entity', type: 'checkbox' },
        ],
      },
      {
        path: 'nonLitigation.entityBlocks',
        label: 'For any entities listed above',
        type: 'repeatable',
        colSpan: 2,
        visibleIf: {
          all: [
            { path: 'nonLitigation.adversarial', op: 'is_true' },
            { path: 'nonLitigation.adverseParties', op: 'some', value: { path: 'isEntity', op: 'is_true' } },
          ],
        },
        addLabel: 'Add entity detail',
        fields: [
          { path: 'entity', label: 'Entity', type: 'text' },
          { path: 'shareholders', label: 'Shareholders', type: 'tags' },
          { path: 'officers', label: 'Officers', type: 'tags' },
          { path: 'directors', label: 'Directors', type: 'tags' },
          { path: 'partners', label: 'Partners', type: 'tags' },
          { path: 'members', label: 'Members', type: 'tags' },
          { path: 'employeeContact', label: 'Employee Contact', type: 'text' },
        ],
      },
      {
        path: 'nonLitigation.ownOtherBusiness',
        label: 'Do the individuals listed in section A own any other business?',
        type: 'yesno',
        colSpan: 2,
      },
      {
        path: 'nonLitigation.otherBusinessNames',
        label: 'Names of those businesses',
        type: 'tags',
        colSpan: 2,
        visibleIf: { path: 'nonLitigation.ownOtherBusiness', op: 'is_true' },
      },
      {
        path: 'nonLitigation.neutrals',
        label: 'Neutral individuals/entities involved (if any)',
        type: 'tags',
        colSpan: 2,
      },
      {
        path: 'nonLitigation.handledBefore',
        label: 'Have you handled this type of matter in the past?',
        type: 'yesno',
        colSpan: 2,
        help: 'If No, Practice Group Leader or team leader approval is required before the file opens.',
      },
    ],
  },

  // ── 10 ──────────────────────────────────────────────────────────────────
  {
    id: 'litigation',
    title: 'Litigation',
    visibleIf: isLitigation,
    fields: [
      {
        path: 'litigation.opposingParties',
        label: 'Opposing parties',
        type: 'repeatable',
        colSpan: 2,
        addLabel: 'Add opposing party',
        fields: [
          { path: 'name', label: 'Name', type: 'text' },
          { path: 'isEntity', label: 'Is an entity', type: 'checkbox' },
        ],
      },
      {
        path: 'litigation.opposingEntityPrincipals',
        label: 'If an opposing party is an entity: its officers, directors and members',
        type: 'repeatable',
        colSpan: 2,
        visibleIf: { path: 'litigation.opposingParties', op: 'some', value: { path: 'isEntity', op: 'is_true' } },
        addLabel: 'Add entity principals',
        fields: [
          { path: 'entity', label: 'Entity', type: 'text' },
          { path: 'officers', label: 'Officers', type: 'tags' },
          { path: 'directors', label: 'Directors', type: 'tags' },
          { path: 'members', label: 'Members', type: 'tags' },
        ],
      },
      {
        path: 'litigation.opposingCounsel',
        label: 'Opposing counsel (if known)',
        type: 'text',
        colSpan: 2,
        // The label explicitly permits an unknown answer; keep it on the existing
        // explain-if-blank path even if a legacy field-rule override says required.
        requirementOverride: 'soft_required',
      },
      { path: 'litigation.neutrals', label: 'Neutral parties (if any)', type: 'tags', colSpan: 2 },
      {
        path: 'litigation.handledBefore',
        label: 'Have you handled this type of litigation in the past?',
        type: 'yesno',
        colSpan: 2,
        help: 'If No, Practice Group Leader or team leader approval is required before the file opens.',
      },
      { path: 'litigation.caseNo', label: 'Case No.', type: 'text' },
      {
        path: 'litigation.court',
        label: 'Court',
        type: 'text',
        // A court may not exist yet when a new litigation matter is opened.
        requirementOverride: 'soft_required',
      },
    ],
  },

  // ── 11 ──────────────────────────────────────────────────────────────────
  {
    id: 'fees',
    title: 'Fees',
    fields: [
      {
        path: 'fees.amountInDispute',
        label: 'What is the amount of the dispute?',
        type: 'money',
        visibleIf: isLitigation,
      },
      {
        path: 'fees.estimatedTotalFees',
        label: 'Estimated total fees and costs',
        type: 'money',
        visibleIf: { path: 'client.isFlatFee', op: 'is_false' },
      },
      {
        path: 'fees.clientInformedOfFees',
        label: 'Has the client been informed of the total fees and costs?',
        type: 'yesno',
        colSpan: 2,
      },
      {
        path: 'fees.evergreenRetainer',
        label: 'Are we obtaining an evergreen retainer held in trust pending resolution?',
        type: 'yesno',
        colSpan: 2,
      },
    ],
  },

  // ── 12 ──────────────────────────────────────────────────────────────────
  {
    id: 'litigationHold',
    title: 'Litigation Hold Letter',
    fields: [
      { path: 'litigationHold.toBeSent', label: 'Litigation Hold Letter to be sent?', type: 'yesno', colSpan: 2 },
      {
        path: 'litigationHold.explanation',
        label: 'If no, explain why',
        type: 'textarea',
        colSpan: 2,
        visibleIf: { path: 'litigationHold.toBeSent', op: 'is_false' },
      },
    ],
  },

  // ── 13 ──────────────────────────────────────────────────────────────────
  {
    id: 'areasOfLaw',
    title: 'Area of Law',
    description: 'The list is admin-editable from the Admin panel.',
    fields: [
      { path: 'areasOfLaw', label: 'Area of law', type: 'areaOfLawSelect', colSpan: 2 },
      {
        path: 'areaOfLawOther',
        label: 'Other — describe',
        type: 'text',
        colSpan: 2,
        visibleIf: { path: 'areasOfLaw', op: 'eq', value: 'other' },
      },
      {
        path: 'areaOfLawOtherReason',
        label: 'Reason for selecting Other',
        type: 'textarea',
        colSpan: 2,
        visibleIf: { path: 'areasOfLaw', op: 'eq', value: 'other' },
      },
    ],
  },

  // ── 14 ──────────────────────────────────────────────────────────────────
  {
    id: 'filesToBeOpened',
    title: 'Files To Be Opened',
    fields: [
      { path: 'filesToBeOpened.returnFileTo', label: 'Return opened file to', type: 'text' },
      { path: 'filesToBeOpened.createdByName', label: 'File created by', type: 'text' },
      { path: 'filesToBeOpened.createdDate', label: 'Date', type: 'date' },
      {
        path: 'filesToBeOpened.matterName',
        label: 'Matter Name',
        type: 'text',
        maxLength: 40,
        colSpan: 2,
        help: 'Maximum 40 characters.',
      },
      {
        path: 'filesToBeOpened.subtypes',
        label: 'File subtypes to open',
        type: 'multicheck',
        colSpan: 2,
        optionsFrom: 'fileSubtypes',
      },
    ],
  },

  // ── 15 ──────────────────────────────────────────────────────────────────
  {
    id: 'origination',
    title: 'Origination of File / Working Attorneys',
    fields: [
      {
        path: 'origination.responsiblePartnerUid',
        label: 'Responsible Partner',
        type: 'userSelect',
        // Mirrored from header.responsiblePartnerUid by deriveMatter.js's
        // applyResponsiblePartnerMirror — this is no longer an independently editable
        // field, just a read-only reflection so the roster name still renders here.
        derivedFrom: 'header.responsiblePartnerUid',
        help: 'Matches the Responsible Partner set in Header & Billing above — change it there.',
      },
      {
        path: 'origination.workingAttorneys',
        label: 'Working Attorney(s) / Paralegal(s) and origination split',
        type: 'originationSplit',
        colSpan: 2,
        help: 'The origination split must total exactly 100%, and no one attorney may hold more than the firm maximum — so the credit has to be shared.',
      },
    ],
  },

  // ── 16 ──────────────────────────────────────────────────────────────────
  {
    id: 'attachments',
    title: 'Attachments',
    description:
      'Upload conflicts check documentation and the signed engagement letter, plus any other ' +
      'supporting documents. Each upload is kept as its own dated entry — re-uploading a ' +
      'corrected document adds a new one rather than replacing the old, so there’s a record ' +
      'if a matter is kicked back over a document issue. Files are stored in Firebase Storage ' +
      'under matters/{matterId}/{category}/.',
    fields: [
      {
        path: 'attachments.conflictsCheck',
        label: 'Conflicts check documents',
        type: 'attachments',
        category: 'conflicts_check',
        colSpan: 2,
        help: 'Conflicts search results, waivers, or related correspondence. Multiple files allowed.',
      },
      {
        path: 'attachments.engagementLetter',
        label: 'Engagement letter',
        type: 'attachments',
        category: 'engagement_letter',
        colSpan: 2,
        help: 'The signed engagement letter (and any amendments). Multiple files allowed.',
      },
      {
        path: 'attachments.other',
        label: 'Other documents',
        type: 'attachments',
        category: 'other',
        colSpan: 2,
        help: 'Anything else relevant to opening this matter.',
      },
    ],
  },
];

/** Flat list of every real (non-notice) field, used by the rules engine and admin editor. */
export const ALL_FIELDS = FORM_SECTIONS.flatMap((section) =>
  section.fields
    .filter((f) => f.path && f.type !== 'notice' && f.type !== 'attachments')
    .map((f) => ({ ...f, sectionId: section.id, sectionTitle: section.title }))
);

/**
 * Today as YYYY-MM-DD in the browser's own time zone. Not toISOString(), which is UTC:
 * from about 5 PM Pacific that is already tomorrow, and the Date field defaulted to it.
 */
export function localIsoDate(now = new Date()) {
  const pad = (n) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/** A blank matter with the shape the engine and security rules expect. */
export function emptyMatter(uid) {
  return {
    status: 'draft',
    createdBy: uid,
    collaboratorUids: [],
    header: {
      date: localIsoDate(),
      practiceGroupId: '',
      additionalPracticeGroupIds: [],
      engagementLetter: { signedOnFile: null },
      isNewClient: false,
      isNewMatter: false,
    },
    client: { address: {}, insurance: {} },
    representation: {},
    wallBuilder: { practiceGroupIds: [], personUids: [], notes: '' },
    clientSource: {},
    priorRepresentation: {},
    parties: { list: [] },
    conflictCheck: { hitsFound: null },
    matterType: { isLitigation: false, isNonLitigation: false, selection: '' },
    nonLitigation: {},
    litigation: {},
    fees: {},
    litigationHold: {},
    areasOfLaw: '',
    areaOfLawOther: '',
    areaOfLawOtherReason: '',
    filesToBeOpened: { subtypes: [] },
    origination: { workingAttorneys: [] },
    clientNumber: null,
    matterNumber: null,
  };
}
